import Util from 'cleave.js/src/utils/Util';
import { networkKeys, NodeKeys } from '../actions/inventories';
import * as QueryString from 'query-string';

// devices images
import baseband from '../semantic-ui/site/assets/ibwave/baseband.svg';
import indoor_radio_unit_4G from '../semantic-ui/site/assets/ibwave/indoor_radio_unit_4G.svg';
import outdoor_radio_unit_4G from '../semantic-ui/site/assets/ibwave/outdoor_radio_unit_4G.svg';
import centralised_unit_5G_corning from '../semantic-ui/site/assets/ibwave/centralised_unit_5G_corning.svg';
import outdoor_radio_unit_5G from '../semantic-ui/site/assets/ibwave/outdoor_radio_unit_5G.svg';
import indoor_radio_unit_5G_corning from '../semantic-ui/site/assets/ibwave/indoor_radio_unit_5G_corning.svg';
import { history } from '../store/configureStore';
import legendRSRP from '../semantic-ui/site/assets/legend-RSRP.png';
import legendSNIR from '../semantic-ui/site/assets/legend-SNIR.png';
import { osndLocalStorage, storageConstant } from './localStorageUtils';

export const deviceIconMappings = {
    [NodeKeys.BASEBAND]: baseband,
    [NodeKeys.INDOOR_RADIO_UNIT]: indoor_radio_unit_4G,
    [NodeKeys.SMALL_CELL_RADIO_INDOOR]: indoor_radio_unit_4G,
    [NodeKeys.OUTDOOR_RADIO_4G]: outdoor_radio_unit_4G,
    [NodeKeys.CENTRALIZED_UNIT_5G]: centralised_unit_5G_corning,
    [NodeKeys.OUTDOOR_RADIO_5G]: outdoor_radio_unit_5G,
    [NodeKeys.SMALL_CELL_RADIO_NODE_5G]: indoor_radio_unit_5G_corning
};

export const formatters = Object.freeze({
    ICCID: {
        numericOnly: true,
        blocks: [7, 7, 6],
        delimiters: [' - ']
    },
    IMSI: {
        numericOnly: true,
        blocks: [3, 3, 9],
        delimiter: '-'
    },
    IMEI: {
        numericOnly: true,
        blocks: [2, 6, 6, 2],
        delimiter: '-'
    },
    ACTIVITY: {
        numericOnly: true,
        blocks: [4, 2, 2],
        delimiter: '-'
    }
});

export const format = (value = '', type = '') => {
    const formatter = formatters[type];

    let formatValue;

    if (formatter !== undefined) {
        const { blocks = [], delimiter = '', delimiters = [] } = formatter;
        const blocksLength = blocks.length;
        const delimiterLazyShow = false;

        formatValue = Util.getFormattedValue(value, blocks, blocksLength, delimiter, delimiters, delimiterLazyShow).trim();
    } else {
        formatValue = value;
    }

    return formatValue.trim();
};

export const standardizeLabel = (label) => {
    let words = label?.split(' ');
    let convertedwords = words?.map((word, index) => {
        if (index === 0) {
            return word;
        } else if (/[A-Z]{2,}/.test(word)) {
            return word;
        } else {
            return word?.charAt(0).toLowerCase() + word?.slice(1);
        }
    });
    return convertedwords?.join(' ');
};

const portalTitle = 'Verizon On Site Network Dashboard';

export const pageTitle = (title) => {
    if (title) {
        return `${portalTitle} | ${title}`;
    }

    return portalTitle;
};

export const nodeNameLookup = {
    operationalState: 'Operational state',
    nodeType: 'HW node type',
    partName: 'HW part name',
    partCode: 'HW part # / part code',
    prodRevision: 'Product revision',
    serialNumber: 'HW serial number',
    vendorName: 'HW vendor name',
    swRelease: 'SW release',
    partId: 'Part Id',
    manufacturer: 'Manufacturer',
    model: 'Model',
    partType: 'Part type',
    category: 'Category',
    subCategory: 'Sub category'
};

export const cbrsLookup = {
    fcc_id: 'FCC ID',
    cbsd_serial_number: 'CBSD Serial Number',
    latitude: 'Latitude',
    longitude: 'Longitude',
    height: 'Height',
    height_type: 'Height Type',
    horizontal_accuracy: 'Horizontal Accuracy',
    vertical_accuracy: 'Vertical Accuracy',
    indoor_deployment: 'Indoor Deployment',
    antenna_azimuth: 'Antenna Azimuth',
    antenna_tilt: 'Antenna Tilt',
    antenna_gain: 'Antenna Gain',
    eirp_capability: 'EIRP',
    antenna_beamwidth: 'Antenna Beamwidth',
    antenna_model: 'Antenna Model',
    cpi_id: 'CP ID',
    cpi_name: 'CPI Name',
    install_certification_time: 'Install Certification Time'
};

export const roles = Object.freeze({
    ADMIN_USER: 'ROLE_ADMIN_USER', // add/modify/delete users with admin roles

    MANAGE_ADMINS: 'ROLE_MANAGE_ADMINS', // add/modify/delete users with admin roles

    MANAGE_USERS: 'ROLE_MANAGE_USERS', // add/modify/delete users
    VIEW_USERS: 'ROLE_VIEW_USERS', // can view users

    MANAGE_ROLES: 'ROLE_MANAGE_ROLES', // add/modify/delete roles
    VIEW_ROLES: 'ROLE_VIEW_ROLES', // view roles

    MANAGE_LOCATIONS: 'ROLE_MANAGE_LOCATIONS', // add/modify/delete locations (TBD)
    VIEW_LOCATIONS: 'ROLE_VIEW_LOCATIONS', // view locations (TBD)

    VIEW_ALARMING: 'ROLE_VIEW_ALARMING', // view alarming (TBD)
    VIEW_MONITORING: 'ROLE_VIEW_MONITORING', // view monitoring (TBD)
    VIEW_EVENTS: 'ROLE_VIEW_EVENTS', // view events (TBD)

    VIEW_TICKETING: 'ROLE_VIEW_TICKETING', // view ticketing
    MANAGE_TICKETING: 'ROLE_MANAGE_TICKETING', // manage ticketing
    VIEW_MANAGE_ALERTS: 'ROLE_VIEW_MANAGE_ALERTS', // view and manage alerts
    MANAGE_SERVICE_LEVEL_RESORATION_TIME: 'ROLE_MANAGE_SERVICE_LEVEL_RESORATION_TIME', // manage Restoration Time tab
    MANAGE_SERVICE_LEVEL_RESPONSE_NOTIF_TIME: 'ROLE_MANAGE_SERVICE_LEVEL_RESPONSE_NOTIF_TIME', // manage Response Notification Time tab
    MANAGE_SERVICE_LEVEL_AVAILABILITY: 'ROLE_MANAGE_SERVICE_LEVEL_AVAILABILITY', // manage Availability tab

    VIEW_DIRECTORY_SYNC_SCIM: 'ROLE_VIEW_DIRECTORY_SYNC_SCIM',

    MANAGE_SUBSCRIBERS: 'ROLE_MANAGE_SUBSCRIBERS', // modify subscribers
    VIEW_SUBSCRIBERS: 'ROLE_VIEW_SUBSCRIBERS', // view subscribers

    MANAGE_BACKUPS: 'ROLE_MANAGE_BACKUPS', // add/modify/delete backups
    VIEW_BACKUPS: 'ROLE_VIEW_BACKUPS', // view backups

    VIEW_ESIM_SUPPORT: 'ROLE_VIEW_ESIM_SUPPORT', // view eSims

    VIEW_DEVICE_MONITORING: 'ROLE_VIEW_DEVICE_MONITORING', // view devices
    VIEW_INVENTORY: 'ROLE_VIEW_INVENTORY', // view inventory
    VIEW_FLOORPLANS: 'ROLE_VIEW_FLOORPLANS',
    VIEW_NETWORK_PERFORMANCE: 'ROLE_VIEW_NETWORK_PERFORMANCE',

    VIEW_MEC_PERFORMANCE: 'ROLE_VIEW_MEC_PERFORMANCE',
    VIEW_MEC_REPORTS: 'ROLE_VIEW_MEC_REPORTS',
    MANAGE_MEC_USAGE_REPORTS: 'ROLE_MANAGE_MEC_USAGE_REPORTS',
    MANAGE_MEC_PERFORMANCE: 'ROLE_MANAGE_MEC_PERFORMANCE',
    MANAGE_MEC_PERFORMANCE_OSND: 'ROLE_MANAGE_MEC_PERFORMANCE_OSND',

    MANAGE_NETWORK_5G_NSA_SUPPORT: 'ROLE_MANAGE_NETWORK_5G_NSA_SUPPORT', // manage 5G NSA inventory
    VIEW_KPI: 'ROLE_VIEW_NETWORK_PEFORMANCE_MONITORING_KPI', // view KPI
    MANAGE_SIM_PROVISIONING: 'ROLE_MANAGE_SIM_PROVISIONING_OUTSIDE_GUARDEDLINK', // view SIM Provisioning
    MANAGE_CBRS_SUPPORT: 'ROLE_MANAGE_CBRS_SUPPORT',
    VIEW_KPI_EXFO: 'ROLE_MANAGE_EXFO_KPI_INTEGRATION',
    MANAGE_ALERTS: 'ROLE_MANAGE_ALERTS',
    MANAGE_FLOOR_PLANS: 'ROLE_MANAGE_FLOOR_PLANS',
    MANAGE_ATTACHED_DEVICES: 'ROLE_MANAGE_ATTACHED_DEVICES',
    MANAGE_UL_THROUGHPUT: 'ROLE_MANAGE_UL_THROUGHPUT',
    MANAGE_DL_THROUGHPUT: 'ROLE_MANAGE_DL_THROUGHPUT',
    MANAGE_ATTACH_SUCCESS: 'ROLE_MANAGE_ATTACH_SUCCESS',
    MANAGE_TOP_DEVICES_EP5G: 'ROLE_MANAGE_TOP_DEVICES_EP5G',
    MANAGE_USERA_USERC_PERF: 'ROLE_MANAGE_USERA_USERC_PERF',
    MANAGE_DEVICES: 'ROLE_MANAGE_DEVICES',
    MANAGE_OUTDOOR_MAP: 'ROLE_MANAGE_OUTDOOR_MAP',
    MANAGE_IMSI_DATA_USAGE: 'ROLE_MANAGE_IMSI_DATA_USAGE',
    MANAGE_SERVICE_LEVEL_AVAILABILITY_PLUS: 'ROLE_MANAGE_SERVICE_LEVEL_AVAILABILITY_PLUS',
    MANAGE_MULTI_NIB_COMBINED_VIEW: 'ROLE_MANAGE_MULTI_NIB_COMBINED_VIEW',

    KPI_ACCESSIBILITY: 'ROLE_VIEW_KPI_ACCESSIBILITY',
    KPI_MOBILITY: 'ROLE_VIEW_KPI_MOBILITY',
    KPI_RETAINABILITY: 'ROLE_VIEW_KPI_RETAINABILITY',
    KPI_OVERALL_E2E_ACCESSIBILITY: 'ROLE_VIEW_KPI_OVERALL_E2E_ACCESSIBILITY',
    VIEW_NEW_PERFORMANCE_PAGE: 'ROLE_VIEW_NEW_PERFORMANCE_PAGE'
});

/**
 * Make sure one or all needs array exists in has array
 *
 * @param hasArr
 * @param needArr
 * @param or - true if at least one in needArr, false if all in needArr
 * @returns {boolean}
 */
export const existsIn = (hasArr = [], needArr = [], or = true) => {
    const needs = typeof needArr === 'string' ? [needArr] : needArr;
    const has = typeof hasArr === 'string' ? [hasArr] : hasArr;

    if (or) {
        return needs.length === 0 || needs.some((need) => has.indexOf(need) > -1);
    }
    return needs.length === 0 || needs.every((need) => has.indexOf(need) > -1);
};

/**
 * Caution because worse case will loop two times.
 *
 * @param arr1
 * @param arr2
 * @returns {boolean}
 */
export const equalsIgnoreOrder = (arr1, arr2) => {
    return arr1.length === arr2.length && arr1.every((value) => arr2.indexOf(value) > -1) && arr2.every((value) => arr1.indexOf(value) > -1);
};

export const objectsEqual = (obj1, obj2) => {
    return Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every((p) => obj1[p] === obj2[p]);
};

export const getAllRoles = () => ({ ...roles });

export const userAllowed = (userRoles, requiredRoles = [], or = true) => existsIn(userRoles, requiredRoles, or);

export const fileSize = (bytes, si = true) => {
    const thresh = si ? 1000 : 1024;
    let sizeBytes = bytes;

    if (Math.abs(sizeBytes) < thresh) {
        return `${sizeBytes} B`;
    }

    const units = si ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    let u = -1;

    do {
        sizeBytes /= thresh;
        u += 1;
    } while (Math.abs(sizeBytes) >= thresh && u < units.length - 1);

    return `${sizeBytes.toFixed(1)} ${units[u]}`;
};

export const nth = (value) => {
    if (value > 3 && value < 21) {
        return 'th';
    }

    switch (value % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

export const day = (value) => {
    switch (value) {
        case '1':
            return 'Sunday';
        case '2':
            return 'Monday';
        case '3':
            return 'Tuesday';
        case '4':
            return 'Wednesday';
        case '5':
            return 'Thursday';
        case '6':
            return 'Friday';
        case '7':
            return 'Saturday';
        default:
            return value;
    }
};

export const getNotificationIcon = (type) => {
    switch (type) {
        case 'success':
            return { className: 'success' };
        case 'info':
            return { className: 'info' };
        case 'warning':
            return { className: 'warning' };
        case 'error':
            return { className: 'error' };
        default:
            return true; // no icon
    }
};

/**
 * Used within UseEffect() functons to make sure we arent sending sort changes if its not actually changing
 *
 * @param sortBy
 * @param currentSortBy
 * @param currentDesc
 * @returns {boolean}
 */
export const isSortByChanged = (sortBy, currentSortBy, currentDesc) => {
    const [sort = { id: null, desc: false }] = sortBy;
    return currentSortBy !== sort.id || currentDesc !== sort.desc;
};

/**
 * Admin users will have enterpriseId start of the paths
 *
 * @param enterpriseId
 * @param path
 * @returns {string|*}
 */
export const getPath = (enterpriseId, path) => {
    return enterpriseId ? `${path}#${enterpriseId}` : path;
};

export const browserRedirect = (path) => {
    window.location.href = path;
};

export const blockedPaths = ['/logout', '/welcome/', '/reset'];

export const isBlocked = (path) =>
    blockedPaths.includes((blockedPath) => {
        path.startsWith(blockedPath);
    });

const elementNameLookup = {
    hss: 'Home Subscriber Server (HSS)',
    mme: 'Mobility Manager Entity (MME)',
    sapc: 'Policy and Charging Rules Function (PCRF)',
    epg: 'System Architecture Evolution Gateway (SAE-GW)',
    r6675: 'Site Router (SR)',
    enb: 'eNodeB (ENB)',
    '5gnb': '5G NodeB (5GNB)',
    mps: 'Multi Purpose Server',
    dp: 'Local Domain Proxy (LDP)',
    '4g5g': 'ENodeB (ENB), 5G NodeB (5GNB)'
};

export const getNetworkElementType = (node) => {
    let elementName = '';
    if (node && node != '') {
        for (const [key, value] of Object.entries(elementNameLookup)) {
            if (node.toLowerCase().includes(key)) {
                elementName = value;
                break;
            }
        }
    }
    return elementName;
};

const isNotNull = (value) => {
    return !!value && value.toString().toLowerCase() !== 'null' && value.toString().toLowerCase() !== '';
};

const isPositiveNumber = (value) => {
    if (!!value) {
        let n = value;
        if (typeof value !== 'number') {
            n = Math.floor(Number(value));
        }
        return n !== Infinity && n > 0;
    }
    return false;
};

const isMatchesWith = (value, data) => {
    return !!value && data.includes(value.toLowerCase());
};

const isFutureDate = (value, executionDate) => {
    return !!value && new Date(value) > new Date(executionDate);
};

const isInDoor = (data) => {
    return data?.nodeType && data.nodeType.toLowerCase().includes('indoor');
};

const isOutDoor = (data) => {
    return data?.nodeType && data.nodeType.toLowerCase().includes('outdoor');
};

const isGrantRadiating = (node, grant) => {
    let isRadiating = false;
    for (const [key, value] of Object.entries(grant)) {
        if (!!value) {
            if (key === 'heart_beat_rate') {
                isRadiating = isNotNull(value) && isPositiveNumber(value.replace(/\D/g, ''));
            } else if (key === 'transmit_expire_time' || key === 'grant_expire_time') {
                isRadiating = isFutureDate(value, grant?.execution_time);
            } else if (key === 'max_power') {
                if (isInDoor(node)) {
                    isRadiating = value >= 0 && value <= 30;
                } else if (isOutDoor(node)) {
                    isRadiating = value >= 0 && value <= 47;
                }
            } else if (key === 'channel_type') {
                isRadiating = isMatchesWith(value, ['gaa', 'pal']);
            } else if (key === 'admin_state') {
                isRadiating = isMatchesWith(value, ['unlocked']);
            } else if (key === 'cell_cbrs_capable') {
                isRadiating = typeof value === 'boolean';
            } else if (key === 'channel_bandwidth') {
                isRadiating = value === 10000 || value === 20000 || value === 30000 || value === 40000;
            } else if (key === 'cbrs_frequency_band') {
                isRadiating = Number(value.replace(/\D/g, '')) === 48;
            } else if (key === 'operationalState') {
                isRadiating = isMatchesWith(value, ['authorized', 'granted']);
            } else if (key === 'frequency_range_granted') {
                const [lowBand, highBand] = value.split('-');
                const lBand = Number(lowBand.replace(/\D/g, ''));
                const hBand = Number(highBand.replace(/\D/g, ''));
                isRadiating = lBand >= 3550 && hBand <= 3700;
            } else if (key === 'earfcn') {
                isRadiating = value >= 55240 && value <= 56740;
            }

            if (!isRadiating) {
                console.log(`Non Radiating reason - ${key} - ${value}, Grant - ${grant?.grant_id}`);
                return false;
            }
        }
    }
    return isRadiating;
};

export const isRadiaingIcon = (node) => {
    let result = false;
    if (!node?.cbrsDynamicDataList || node.cbrsDynamicDataList.length === 0) return false;

    const flatArr = node.cbrsDynamicDataList.reduce((acc, curVal) => {
        return curVal?.grantList?.length > 0 ? acc.concat(curVal.grantList) : acc;
    }, []);

    if (flatArr.length === 0) return false;

    for (let grant of flatArr) {
        result = isGrantRadiating(node, grant);
        if (result) {
            break;
        }
    }
    return result;
};

export const isCBRSSettingIconEnabled = (node, data) => {
    const flatArr =
        node?.cbrsDynamicDataList?.reduce((acc, curVal) => {
            return curVal?.grantList?.length > 0 ? acc.concat(curVal.grantList) : acc;
        }, []) || [];
    return data?.operationalState === 'ENABLED' && !!data.cbrsDynamicDataList && flatArr?.length > 0;
};

export const removeDuplicateGrants = (cbrsDynamicData) => {
    if (cbrsDynamicData && cbrsDynamicData.length > 0) {
        for (let index = 0; index < cbrsDynamicData.length; index++) {
            const sector = cbrsDynamicData[index];
            if (sector?.grantList?.length > 0) {
                // unique grant ids
                let grants = [...new Map(sector.grantList.map((item) => [item['grant_id'], item])).values()];
                sector.grantList = grants.length > 9 ? grants.slice(0, 10) : grants;
            }
            cbrsDynamicData[index] = sector;
        }
    }

    return cbrsDynamicData;
};

export const partition = (array, isValid) => {
    return array.reduce(
        ([pass, fail], elem) => {
            return isValid(elem) ? [[...pass, elem], fail] : [pass, [...fail, elem]];
        },
        [[], []]
    );
};

export const getNodeKey = (node, addPartName = false) => {
    if (!node) {
        return '';
    }
    if (node.nodeType === 'CENTRALIZED_UNIT_5G') {
        return node.partName ? `${node.nodeName}/${node.partName}/${node.serialNumber}` : node.nodeName;
    } else {
        if (node?.partId === node?.unitId && node?.partId === node?.nodeName) {
            // return `${node?.partId} / ${node?.partId}`;
            return node?.partId;
        }
        return node.unitId ? `${node.nodeName}/${node.unitId}/${node.serialNumber}` + (addPartName ? `/${node.partName}` : `` ): node.nodeName;
    }
};

export const extractFloorPlanComponents = (networks, ibwaveResponse, currentFloorPlanId, isIBC) => {
    let ibWaveComponents = [];
    let stackedNodeMap = {};
    let predictionMap = {};
    if (ibwaveResponse?.buildings) {
        ibwaveResponse.buildings.forEach((building) => {
            if (building?.floorPlans) {
                const { buildingId, buildingName, floorPlans } = building;
                const filteredFloorPlans = !!currentFloorPlanId ? floorPlans.filter((el) => el.floorPlanId === currentFloorPlanId) : floorPlans;
                filteredFloorPlans.forEach((floorPlan) => {
                    if (floorPlan?.components?.length > 0) {
                        const { fileLocation, floorPlanId, floorPlanName, components, imageLocationInPixels, predictionMaps } = floorPlan;
                        const updatedComponents = components.map((component) => {
                            const locations = getDeviceLocation(imageLocationInPixels, component.locationPixels);
                            return {
                                ...component,
                                ...locations,
                                buildingId,
                                buildingName,
                                fileLocation,
                                floorPlanId,
                                floorPlanName,
                                nodeName: isIBC ? component.partId : component.nodeName
                            };
                        });
                        ibWaveComponents = [...ibWaveComponents, ...updatedComponents];
                        predictionMap[floorPlanId] = predictionMaps;
                    }
                });
            }
        });
    }

    if (networks && ibWaveComponents?.length > 0) {
        let inventoryDetails = [];
        networks.forEach(({ networkType, parentNodes }) => {
            if (networkType === networkKeys.RADIO_NETWORK) {
                parentNodes.forEach((parentNode) => {
                    if (parentNode?.nodeList) {
                        parentNode.nodeList.forEach((node) => {
                            node.nodeName = isIBC ? node.partId : node.nodeName;
                            inventoryDetails.push(node);
                        });
                    }
                });
            }
        });

        ibWaveComponents = ibWaveComponents.map((ibWaveComponent) => {
            let nextIbWaveComp;
            // const commonKey = ibWaveComponent.nodeName + (ibWaveComponent.unitId || ibWaveComponent.partId);
            const manufacturer = ibWaveComponent.manufacturer;
            const inventoryDetail = inventoryDetails.find(
                (invNode) => getInventoryCommonKey(manufacturer, invNode, isIBC) === getIbWaveCommonKey(manufacturer, ibWaveComponent, isIBC)
            );
            const { nodeType, nodeName, unitId, nibId, X, Y, ...rest } = ibWaveComponent;

            if (!!inventoryDetail) {
                nextIbWaveComp = { ...ibWaveComponent, inventoryDetail };
            } else {
                nextIbWaveComp = { ...ibWaveComponent, inventoryDetail: rest };
            }

            if (stackedNodeMap[`${X}${Y}`]) {
                stackedNodeMap[`${X}${Y}`].push(nextIbWaveComp);
            } else {
                stackedNodeMap[`${X}${Y}`] = [nextIbWaveComp];
            }

            return nextIbWaveComp;
        });
    }

    return { ibWaveComponents, stackedNodeMap, predictionMap };
};

export const getStackedOperationalStatusColorCode = (stackedDevices) => {
    const statuses = stackedDevices.map((d) => d.inventoryDetail?.operationalState || '');
    const counts = {};
    for (const status of statuses) {
        counts[status] = counts[status] ? counts[status] + 1 : 1;
    }
    if (counts['ENABLED'] === stackedDevices.length) return '#00AC3E';
    else if (counts['DISABLED'] === stackedDevices.length) return '#d52b1e';
    else if (counts[''] === stackedDevices.length) return 'grey';
    return 'orange';
};

export const getOperationalStatusColorCode = (val = '') => {
    if (val === 'ENABLED') return '#00AC3E';
    else if (val === 'DISABLED') return '#d52b1e';
    return 'grey';
};

export const getDeviceLocation = (imgLocation, deviceLocation) => {
    const locations = {};
    const imagePixels = imgLocation && imgLocation.substring(7, imgLocation.length - 1);
    const devicePixels = deviceLocation && deviceLocation.substring(7, deviceLocation.length - 1);

    if (imagePixels && devicePixels) {
        const [imageLocationX, imageLocationY] = imagePixels.split(' ');
        const [deviceLocationX, deviceLocationY] = devicePixels.split(' ');
        // Icon width and height are 50px so removing 30px to identify correct location.
        locations.X = deviceLocationX - imageLocationX - 25;
        locations.Y = deviceLocationY - imageLocationY - 25;
    }
    return locations;
};

export const mapOrder = (array, order, key) => {
    array.sort(function (a, b) {
        var A = a[key],
            B = b[key];

        if (order.indexOf(A) > order.indexOf(B)) {
            return 1;
        } else {
            return -1;
        }
    });

    return array;
};

export const isValidIP = (ipAddress) => {
    return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
        ipAddress
    );
};

export const nodeAccess = (nodeName) => {
    return nodeName.includes('ENB') ? '4G' : nodeName.includes('5GNB') ? '5G' : '4G5G';
};

export const DEFAULT_SIM_PREFERENCE = (isAllLocationMultiSIM) => [
    { id: 'id', visible: true },
    { id: 'iccid', visible: true },
    { id: 'imsi', visible: false },
    { id: 'name', visible: false },
    { id: 'feature', visible: false },
    { id: 'profile', visible: false },
    { id: 'ipAddress', visible: true },
    { id: 'defaultApn', visible: true },
    ...(isAllLocationMultiSIM ? [{ id: 'activeLocations', visible: true }] : [{ id: 'status', visible: true }]),
    { id: 'view', visible: true }
];

export const isCoreComponent = (component) => {
    return (
        component.unitId?.toUpperCase().includes('CORE') ||
        component.partId?.toUpperCase().includes('CORE') ||
        component.manufacturer?.toUpperCase().includes('DELL')
    );
};

export const traceCoreComponents = (ibResponse, floorPlanId) => {
    let isCoreCompExists = false;
    (ibResponse.buildings || []).forEach((b) => {
        (b.floorPlans || [])
            .filter((f) => f.floorPlanId === floorPlanId)
            .forEach((f) => {
                if (f.components?.some((c) => isCoreComponent(c))) {
                    isCoreCompExists = true;
                }
            });
    });
    return isCoreCompExists;
};

export const getInventoryCommonKey = (manufacturer, invNode, isIBC) => {
    if (isIBC) {
        return invNode.nodeName + invNode.partId;
    }
    if (manufacturer.includes('Ericsson')) {
        return invNode.nodeName + (invNode.positionInformation + invNode.unitId);
    } else if (manufacturer.includes('ZT Systems')) {
        return invNode.nodeName + (invNode.locationId || invNode.unitId);
    } else if (manufacturer.includes('SpiderCloud Wireless')) {
        return invNode.nodeName + (invNode.nodeIndex || invNode.unitId);
    } else {
        return invNode.nodeName + (invNode.unitId || invNode.positionInformation || invNode.locationId);
    }
};

export const getIbWaveCommonKey = (manufacturer, ibWaveNode, isIBC) => {
    if (isIBC) {
        return ibWaveNode.nodeName + ibWaveNode.partId;
    }
    if (manufacturer.includes('Ericsson')) {
        return ibWaveNode.nodeName + (ibWaveNode.partId + ibWaveNode.unitId);
    } else if (manufacturer.includes('ZT Systems')) {
        return ibWaveNode.nodeName + (ibWaveNode.partId || ibWaveNode.unitId);
    } else if (manufacturer.includes('SpiderCloud Wireless')) {
        return ibWaveNode.nodeName + (ibWaveNode.partId.substring(2) || ibWaveNode.unitId);
    } else {
        return ibWaveNode.nodeName + (ibWaveNode.unitId || ibWaveNode.positionInformation || ibWaveNode.locationId);
    }
};

export const predictionLegendMap = {
    RSRP: {
        label: 'RSRP [dBm]',
        src: legendRSRP
    },
    SNIR: {
        label: 'SNIR [dB]',
        src: legendSNIR
    }
};

export const sortByKey = (unorderedList, key, asc = true) => {
    return unorderedList?.sort((a, b) => {
        if (asc) {
            return a[key].localeCompare(b[key], undefined, {
                numeric: true,
                sensitivity: 'base'
            });
        } else {
            return b[key].localeCompare(a[key], undefined, {
                numeric: true,
                sensitivity: 'base'
            });
        }
    });
};

export const NetworkType = {
    LTE: 'LTE',
    NSA_5G: 'NSA_5G',
    SA_5G: 'SA_5G'
};

export const isEmptyObject = (obj) => Object.keys(obj)?.length === 0;

export const rolesCategories = (permissions) => {
    const subCategoryMap = new Map();
    permissions.forEach((permission) => {
        const { subCategory, category } = permission;
        let smallHeader;
        if (!subCategory) {
            smallHeader = category;
        } else {
            smallHeader = subCategory;
        }
        if (subCategoryMap.has(smallHeader)) {
            subCategoryMap.set(smallHeader, [...subCategoryMap.get(smallHeader), permission]);
        } else {
            subCategoryMap.set(smallHeader, [permission]);
        }
    });

    const categoryMap = new Map();

    [...subCategoryMap.values()].forEach((value) => {
        const { category } = value[0];

        if (categoryMap.has(category)) {
            categoryMap.set(category, [...categoryMap.get(category), value]);
        } else {
            categoryMap.set(category, [value]);
        }
    });

    return categoryMap;
};

/**
 * round off given number to roundOffNum
 * @param num
 * @param roundOffNum
 * @returns {number}
 */
export const roundOff = (num, roundOffNum) => {
    if (num && roundOffNum) {
        return Math.ceil(num / roundOffNum) * roundOffNum;
    }
    return num;
};

export const buildPageUrl = (auth, page, params) => {
    let path = page;
    const { pageNum = 1, count, status, search, sort, desc, fromDate, toDate, tab, ...rest } = params;

    const paramsObj = {
        status // blank is removed below
    };

    if (pageNum > 1) {
        // only if greater then 1
        path += `/${pageNum}`;
    }

    if (count !== 10 || count > 100) {
        // only if not 10 and less than 100
        paramsObj.count = count;
    }

    if (desc === true) {
        // only add if true
        paramsObj.desc = '1';
    }

    if (tab) {
        paramsObj.tab = tab;
    }

    if (search) {
        Object.keys(search).forEach((searchKey) => {
            paramsObj[searchKey] = search[searchKey] || null;
        });
    }

    if (!!fromDate) {
        paramsObj.fromDate = fromDate;
    }

    if (!!toDate) {
        paramsObj.toDate = toDate;
    }

    const queryParams = QueryString.stringify(paramsObj, {
        skipNull: true,
        skipEmptyString: true
    });

    if (queryParams !== '') {
        path += `?${queryParams}`;
    }

    const { enterpriseId } = auth;

    if (enterpriseId) {
        path = `${path}#${enterpriseId}`;
    }

    return path;
};

export const redirectTo = (path, newTab = false) => {
    if (newTab) {
        window.open(path, '_blank');
    } else {
        history.push(path);
    }
};

export const getMainSubtitleClass = (count) => {
    return count > 99999 ? 'subtitle-main-small' : 'subtitle-main';
};

export const getSWTitleClass = (plotWidth, isMain) => {
    return plotWidth <= 100 ? (isMain ? 'title-main-small-pie' : 'title-sub-small-pie') : isMain ? 'title-main' : 'title-sub';
};

export const isAllLocations = () => {
    const linkedEnterprises = osndLocalStorage.getObject(storageConstant.LINKED_ENTERPRISES);
    return !!linkedEnterprises?.find((enterprise) => enterprise.enterpriseId === 'all_locations')?.selected;
};

export const getLocations = () => {
    const linkedEnterprises = osndLocalStorage.getArray(storageConstant.LINKED_ENTERPRISES);
    return linkedEnterprises;
};

export const getSelectedLocation = () => {
    const linkedEnterprises = osndLocalStorage.getArray(storageConstant.LINKED_ENTERPRISES);
    return linkedEnterprises?.length > 0 ? linkedEnterprises.find((el) => el.selected) : '';
};

export const getLocationList = () => {
    const linkedEnterprises = osndLocalStorage
        .getArray(storageConstant.LINKED_ENTERPRISES)
        .filter((el) => el.enterpriseId !== 'all_locations')
        .map((el) => el.siteName || el.enterpriseName);
    return linkedEnterprises?.length > 0 ? linkedEnterprises : [];
};

export const getLocationOptions = (includesAll = false) => {
    let linkedEnterprises = osndLocalStorage.getArray(storageConstant.LINKED_ENTERPRISES);
    if (!includesAll) {
        linkedEnterprises = linkedEnterprises?.filter((el) => el.enterpriseId !== 'all_locations');
    }
    return linkedEnterprises?.map((link) => ({
        key: link.enterpriseId,
        value: link.enterpriseId,
        text: link.siteName || link.enterpriseName
    }));
};

export const getEnterpriseName = (enterpriseId) => {
    const linkedEnterprises = osndLocalStorage.getObject(storageConstant.LINKED_ENTERPRISES);
    const enterprise = linkedEnterprises.find((enterprise) => enterprise.enterpriseId === enterpriseId);
    return enterprise?.siteName || enterprise?.enterpriseName;
};

export const toSnakeCase = (str) => {
    return str
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^\w_]/g, '');
};
